import { type Stripe, type StripeElements } from '@stripe/stripe-js'
import { action, computed, observable, runInAction } from 'mobx'
import { required, validationContext } from 'validx'
import { PaymentRequired } from 'fejl'
import { extractMessageFromError } from '@/utils/error-util'
import { task } from 'mobx-task'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { Id } from '@/infra/id'
import type { Activatable } from '@/infra/activation'
import { BehaviorSubject } from 'rxjs'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { filterNonNil } from '@/utils/rx-util'
import type { SnackbarFactory } from '@/features/shell'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ignoreAbortError } from '@/utils/abort-util'
import { FeatureToggles } from '@/features/feature-toggles'

/**
 * State for a card input element.
 */
export interface CardElementState {
  /**
   * Whether the element is completed.
   */
  completed: boolean

  /**
   * Whether the field is ready for user input.
   */
  ready: boolean

  /**
   * Validation error if any.
   */
  error: string | null
}

const CardElementState = {
  empty: {
    completed: false,
    error: null,
    ready: false,
  } as CardElementState,
}

/**
 * View model for adding a new credit/debit card using Stripe.
 * Single-use only. Once a card has been created, the instance should not
 * be reused.
 */
export class CardBillingMethodEditorViewModel implements Activatable {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _created$ = new BehaviorSubject<BillingMethod | null>(null)

  /**
   * The Stripe Elements instance, needed to create a token.
   */
  elements: StripeElements | null = null

  /**
   * Emits the created billing method.
   */
  readonly created$ = this._created$.asObservable().pipe(filterNonNil())

  /**
   * Stripe instance.
   */
  @observable.ref accessor stripe: Stripe | null = null

  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  @observable accessor fullName

  /**
   * Address line 1.
   */
  @observable accessor addressLine1 = ''

  /**
   * Address line 2.
   */
  @observable accessor addressLine2 = ''

  /**
   * Address city.
   */
  @observable accessor city = ''

  /**
   * State or territory, if applicable.
   */
  @observable accessor administrativeArea = ''

  /**
   * Country code.
   */
  @observable accessor country = 'US' // how self-absorbed are we

  /**
   * Zip code.
   */
  @observable accessor zipCode = ''

  /**
   * State for the card number.
   */
  @observable accessor cardNumber = CardElementState.empty

  /**
   * State for the card expiry.
   */
  @observable accessor cardExpiry = CardElementState.empty

  /**
   * State for the card CVC.
   */
  @observable accessor cardCvc = CardElementState.empty

  /**
   * Validation.
   */
  validation = validationContext(this, {
    fullName: [required('Name is required')],
    addressLine1: [required('Address is required')],
    city: [required('City is required')],
    country: [required('Country is required')],
    zipCode: [required('Zip code is required')],
    administrativeArea: [
      (ctx) =>
        this.isAdministrativeAreaRelevant
          ? required('State is required')(ctx)
          : true,
    ],
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly _member: WorkspaceMember,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _billingProfileStore: BillingProfileStore,
  ) {
    this.fullName = this._member.user.displayName
  }

  /**
   * Determines whether all the elements are ready for input.
   */
  @computed
  get elementsReady() {
    return this.cardNumber.ready && this.cardExpiry.ready && this.cardCvc.ready
  }

  /**
   * Whether the state/territory field is relevant.
   */
  @computed
  get isAdministrativeAreaRelevant() {
    return this.country === 'US'
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
    void this.prepare(this._lifecycle.signal).catch(ignoreAbortError())
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Prepares the editor by loading the Stripe SDK.
   */
  prepare = task(async (signal?: AbortSignal) => {
    if (!this.stripe) {
      const stripe = await import('../../stripe/load-stripe').then((m) => {
        signal?.throwIfAborted()
        return m.getStripe()
      })
      signal?.throwIfAborted()
      runInAction(() => (this.stripe = stripe))
    }
  })

  /**
   * Saves the card.
   */
  save = task.resolved(async (): Promise<BillingMethod | null> => {
    if (!this._validate()) {
      return null
    }

    const useStripePaymentMethods = FeatureToggles.paymentMethods()

    const method = useStripePaymentMethods
      ? await this.createCardBillingMethodForMember()
      : await this.createCardBillingMethodForMemberLegacy()

    if (!method) {
      return null
    }

    // Make the new method the default.
    await this._billingProfileStore.changeDefaultMethod(method)

    this._created$.next(method)
    return method
  })

  async createCardBillingMethodForMember() {
    if (!this.stripe || !this.elements) {
      throw new TypeError('Stripe was not loaded.')
    }

    const card = this.elements?.getElement('cardNumber')
    if (!card) {
      return null
    }

    const setupIntent =
      await this._billingProfileStore.setupStripePaymentMethodIntent(
        this._member,
      )

    const confirmation = await this.stripe.confirmCardSetup(
      setupIntent.clientSecret,
      {
        payment_method: {
          card: card,
          billing_details: {
            name: this.fullName,
            address: {
              line1: this.addressLine1,
              line2: this.addressLine2,
              city: this.city,
              country: this.country,
              state: this.isAdministrativeAreaRelevant
                ? this.administrativeArea
                : undefined,
              postal_code: this.zipCode,
            },
          },
        },
      },
    )

    if (confirmation.error) {
      this.validation.addErrors({
        card: [
          confirmation.error.message || 'There was a problem with your card.',
        ],
      })
      return null
    }

    const snackbar = this._newSnackbar()

    return this._billingProfileStore
      .completeSetupStripePaymentMethodIntent(
        confirmation.setupIntent.id,
        this._member,
      )
      .catch((err: unknown) => {
        if (this._maybeSniffMeaningfulInfoFromError(err)) {
          return null
        }
        snackbar.fromError(err)
        throw err
      })
  }

  async createCardBillingMethodForMemberLegacy() {
    if (!this.stripe || !this.elements) {
      throw new TypeError('Stripe was not loaded.')
    }

    const card = this.elements?.getElement('cardNumber')
    if (!card) {
      return null
    }

    const result = await this.stripe.createToken(card, {
      name: this.fullName,
      address_line1: this.addressLine1,
      address_line2: this.addressLine2,
      address_city: this.city,
      address_country: this.country,
      address_state: this.isAdministrativeAreaRelevant
        ? this.administrativeArea
        : '',
      address_zip: this.zipCode,
    })

    if (result.error) {
      this.validation.addErrors({
        card: [result.error.message || 'There was a problem with your card.'],
      })
      return null
    }

    const snackbar = this._newSnackbar()

    return this._billingProfileStore
      .createCardBillingMethodForMember(
        this._member,
        Id.assert(result.token?.id),
      )
      .catch((err: unknown) => {
        if (this._maybeSniffMeaningfulInfoFromError(err)) {
          return null
        }
        snackbar.fromError(err)
        throw err
      })
  }

  /**
   * Validates the form.
   *
   * @private
   */
  @action.bound
  private _validate() {
    this.validation.reset()
    this.validation.validate()

    // Populate validation context based on card element states.
    const toErrors = (displayName: string, state: CardElementState) =>
      state.error
        ? [state.error]
        : !state.completed
          ? [`${displayName} is required.`]
          : []
    this.validation.addErrors({
      cardNumber: toErrors('Card number', this.cardNumber),
      cardExpiry: toErrors('Card expiry', this.cardExpiry),
      cardCvc: toErrors('Card CVC', this.cardCvc),
    })

    return this.validation.isValid
  }

  /**
   * Listen, I don't need your judgment right now.
   *
   * Makeshift way of figuring out what the server error pertains to
   * and assign the error to the right field in the validation context.
   *
   * @param err
   * @private
   */
  @action.bound
  private _maybeSniffMeaningfulInfoFromError(err: unknown) {
    const message = extractMessageFromError(err)
    if (/zip code/i.test(message)) {
      this.validation.addErrors({ zipCode: [message] })
      return true
    }

    if (/card/i.test(message) || err instanceof PaymentRequired) {
      this.validation.addErrors({ cardNumber: [message] })
      return true
    }

    return false
  }
}
